import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { useLang } from "@/context/LanguageContext";
import { fmtRM } from "@/lib/format";
import { Crown, Banknote, Wallet } from "lucide-react";

export default function DirectorStatement() {
  const { t: tr } = useLang();
  const [directors, setDirectors] = useState([]);
  const [grand, setGrand] = useState(null);
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/director/statement")
      .then((r) => {
        const dirs = r.data.directors || [];
        setDirectors(dirs);
        setGrand(r.data.grandTotals || null);
        if (dirs.length) setActive(dirs[0].directorName);
      })
      .finally(() => setLoading(false));
  }, []);

  const current = directors.find((d) => d.directorName === active);
  const t = current?.totals;

  return (
    <Layout>
      <PageHeader
        title={tr("ds.title")}
        subtitle={tr("ds.subtitle")}
        action={
          grand && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-2.5 flex items-center gap-3">
              <Crown className="h-5 w-5 text-amber-600" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-amber-600">{tr("ds.all_profit")}</div>
                <div data-testid="director-grand-profit" className="font-mono-fig text-xl font-bold text-amber-700">
                  {fmtRM(grand.profit || 0)}
                </div>
              </div>
            </div>
          )
        }
      />

      {/* Director selector */}
      {directors.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="director-tabs">
          {directors.map((d) => (
            <button
              key={d.directorName}
              data-testid={`director-tab-${d.directorName}`}
              onClick={() => setActive(d.directorName)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 border ${
                active === d.directorName
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Crown className="h-3.5 w-3.5" />
              {d.directorName}
              <span className="font-mono-fig text-xs opacity-70">({d.totals.sharedLoanCount})</span>
            </button>
          ))}
        </div>
      )}

      {current && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="director-card-capital">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wide">
                <Wallet className="h-4 w-4" /> Director Capital Out
              </div>
              <div className="font-mono-fig text-2xl font-bold text-slate-900 mt-2">{fmtRM(t?.capitalOut || 0)}</div>
              <div className="text-xs text-slate-400 mt-1">{t?.sharedLoanCount || 0} shared loans</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="director-card-interest">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wide">
                <Banknote className="h-4 w-4" /> Interest Collected (Profit)
              </div>
              <div className="font-mono-fig text-2xl font-bold text-green-700 mt-2">{fmtRM(t?.interestCollected || 0)}</div>
              <div className="text-xs text-slate-400 mt-1">{active}'s 50% share</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="director-card-outstanding">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wide">
                <Crown className="h-4 w-4" /> Outstanding (Director Half)
              </div>
              <div className="font-mono-fig text-2xl font-bold text-amber-700 mt-2">{fmtRM(t?.outstanding || 0)}</div>
              <div className="text-xs text-slate-400 mt-1">Still owed to {active}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="director-statement-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-5 py-3 font-medium">Branch</th>
                  <th className="px-3 py-3 font-medium text-right">{tr("ds.shared_loans")}</th>
                  <th className="px-3 py-3 font-medium text-right">{tr("ds.capital_out")}</th>
                  <th className="px-3 py-3 font-medium text-right">Interest (Profit)</th>
                  <th className="px-3 py-3 font-medium text-right">{tr("ds.principal_back")}</th>
                  <th className="px-5 py-3 font-medium text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {current.branches.map((b) => (
                  <tr key={b.branch} data-testid={`director-row-${b.branch}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{b.branchName}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-slate-700">{b.sharedLoanCount}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(b.capitalOut)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig font-semibold text-green-700">{fmtRM(b.profit)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-slate-600">{fmtRM(b.principalCollected)}</td>
                    <td className="px-5 py-3 text-right font-mono-fig text-amber-700">{fmtRM(b.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
              {t && (
                <tfoot>
                  <tr className="bg-slate-900 text-white font-semibold" data-testid="director-totals-row">
                    <td className="px-5 py-3">{tr("ds.all_branches")}</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{t.sharedLoanCount}</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(t.capitalOut)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(t.profit)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig">{fmtRM(t.principalCollected)}</td>
                    <td className="px-5 py-3 text-right font-mono-fig">{fmtRM(t.outstanding)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <p className="text-xs text-slate-400 mt-3 max-w-3xl">
            Shared loans are split 50/50 with Director {active} (customers identified by passport). The branch books
            store the branch's half, which equals the director's matching half shown here.
          </p>
        </>
      )}

      {!loading && directors.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-5 py-12 text-center text-slate-400">
          No director-shared loans yet.
        </div>
      )}
    </Layout>
  );
}
